import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  Stethoscope,
  BrainCircuit,
  Home,
  Menu,
  X,
  Bell,
  CalendarCheck,
  Trash2,
} from "lucide-react";

import logo from "../assets/favicro.png";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // =========================================================
  // FETCH NOTIFICATIONS
  // =========================================================

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        "https://www.careplus.gopillz.com/doctor-notifications",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.notifications)
        ? data.notifications
        : Array.isArray(data?.data)
        ? data.data
        : [];

      setNotifications(list);
    } catch (error) {
      console.error("Notification error:", error);
      setNotifications([]);
    }
  };

  // =========================================================
  // MARK AS READ
  // =========================================================

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");

      await fetch(
        `https://www.careplus.gopillz.com/notifications/${id}/read`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, is_read: true } : n
        )
      );
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  // =========================================================
  // DELETE NOTIFICATION
  // =========================================================

  const deleteNotification = async (id) => {
    try {
      const token = localStorage.getItem("token");

      await fetch(
        `https://www.careplus.gopillz.com/notifications/${id}/delete`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications((prev) =>
        prev.filter((n) => n.id !== id)
      );
    } catch (err) {
      console.error("Delete notification error:", err);
    }
  };

  // =========================================================
  // CLEAR ALL NOTIFICATIONS
  // =========================================================

  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem("token");

      await fetch(
        "https://www.careplus.gopillz.com/notifications/clear-all",
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setNotifications([]);
    } catch (err) {
      console.error("Clear all notifications error:", err);
    }
  };

  // =========================================================
  // UNREAD COUNT
  // =========================================================

  const unreadCount = Array.isArray(notifications)
    ? notifications.filter((n) => !n.is_read).length
    : 0;

  // =========================================================
  // AUTO FETCH
  // =========================================================

  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // =========================================================
  // CLOSE DROPDOWNS
  // =========================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }

      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =========================================================
  // LOGOUT
  // =========================================================

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  // =========================================================
  // NAV BUTTONS
  // =========================================================

  const navButtons = [
    {
      label: "Home",
      path: "/doctor-dashboard",
      icon: <Home className="w-4 h-4 text-blue-500" />,
    },
    {
      label: "Add Patient",
      path: "/add_patient",
      icon: <BrainCircuit className="w-4 h-4 text-green-600" />,
    },
    {
      label: "Lab Manager",
      path: "/add_lab",
      icon: <BrainCircuit className="w-4 h-4 text-purple-600" />,
    },
    {
      label: "Pharmacy Manager",
      path: "/add_pharmacy",
      icon: <BrainCircuit className="w-4 h-4 text-pink-600" />,
    },
    {
      label: "Ambulance Manager",
      path: "/add_ambulance",
      icon: <BrainCircuit className="w-4 h-4 text-red-600" />,
    },
    {
      label: "Nurse Manager",
      path: "/nurse_manager",
      icon: <span className="text-base">🧑‍⚕️</span>,
    },
    {
      label: "Medicine Info",
      path: "/medicine_chatbot",
      icon: <Stethoscope className="w-4 h-4 text-blue-500" />,
    },
    {
      label: "Team Doctor",
      path: "/team_manager",
      icon: <Stethoscope className="w-4 h-4 text-orange-500" />,
    },
    {
      label: "All Appointments",
      path: "/appointment",
      icon: <CalendarCheck className="w-4 h-4 text-blue-500" />,
    },
    {
  label: "Booking Requests",
  path: "/bookings",
  icon: <CalendarCheck className="w-4 h-4 text-green-500" />,
},
  ];

  const currentPath = location.pathname;

  const visibleButtons = navButtons.filter(
    (btn) => btn.path !== currentPath
  );

  const showProfileButton = currentPath !== "/profile";

  return (
    <nav className="bg-white shadow-md px-3 py-2 flex justify-between items-center fixed top-0 w-full z-50">

      {/* LOGO */}
      <div className="flex items-center gap-1 text-xl font-bold text-blue-600">
        <img
          src={logo}
          alt="AICA Logo"
          className="w-6 h-6 object-contain"
        />

        <span>
          AICA<span className="text-gray-700"> | Doctor</span>
        </span>
      </div>

      {/* DESKTOP NAV */}
      <div className="hidden md:flex items-center gap-3">

        {visibleButtons.map((btn, index) => (
          <button
            key={index}
            onClick={() => navigate(btn.path)}
            className={`flex items-center gap-1 text-xs font-medium rounded-lg py-1.5 px-2 ${
              location.pathname === btn.path
                ? "text-blue-600 underline underline-offset-4"
                : "text-gray-700 hover:text-blue-600"
            }`}
          >
            {btn.icon}

            <span className="hidden sm:inline">
              {btn.label}
            </span>
          </button>
        ))}

        {/* ========================================================= */}
        {/* NOTIFICATIONS */}
        {/* ========================================================= */}

        <div ref={notificationRef} className="relative ml-2">

          {/* BELL */}
          <button
            onClick={() =>
              setNotificationOpen(!notificationOpen)
            }
            className="relative p-2 rounded-full hover:bg-gray-100 transition"
          >
            <Bell className="w-5 h-5 text-gray-700" />

            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] px-1.5 rounded-full animate-pulse">
                {unreadCount}
              </span>
            )}
          </button>

          {/* DROPDOWN */}
          {notificationOpen && (
            <div
              className="
                absolute right-0 mt-3
                w-[380px]
                bg-white/95
                backdrop-blur-xl
                rounded-3xl
                shadow-[0_10px_40px_rgba(0,0,0,0.12)]
                border border-gray-100
                overflow-hidden
              "
            >

              {/* HEADER */}
              <div className="px-5 py-4 border-b bg-gradient-to-r from-blue-50 to-white">

                <div className="flex items-center justify-between">

                  <div>
                    <h3 className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-blue-500" />
                      Notifications
                    </h3>

                    <p className="text-[11px] text-gray-500 mt-1">
                      {unreadCount} unread message
                      {unreadCount !== 1 ? "s" : ""}
                    </p>
                  </div>

                  {notifications.length > 0 && (
                    <button
                      onClick={() => {

                        const confirmDelete =
                          window.confirm(
                            "Delete all notifications?"
                          );

                        if (confirmDelete) {
                          clearAllNotifications();
                        }
                      }}
                      className="
                        flex items-center gap-1
                        text-[11px]
                        bg-red-50
                        hover:bg-red-100
                        text-red-600
                        px-3 py-1.5
                        rounded-full
                        transition
                        font-medium
                      "
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* BODY */}
              <div className="max-h-[420px] overflow-y-auto">

                {notifications.length === 0 ? (

                  <div className="p-10 flex flex-col items-center justify-center text-center">

                    <div className="bg-blue-50 p-4 rounded-full mb-3">
                      <Bell className="w-6 h-6 text-blue-400" />
                    </div>

                    <p className="text-sm font-medium text-gray-700">
                      No Notifications
                    </p>

                    <p className="text-xs text-gray-400 mt-1">
                      You're all caught up 🎉
                    </p>

                  </div>

                ) : (

                  notifications.map((n) => (

                    <div
                      key={n.id}
                      className={`
                        px-4 py-4
                        border-b border-gray-100
                        hover:bg-blue-50/70
                        transition-all duration-200
                        ${
                          n.is_read
                            ? "opacity-70 bg-gray-50"
                            : "bg-white"
                        }
                      `}
                    >

                      <div className="flex justify-between items-start">

                        {/* LEFT */}
                        <div
                          className="flex-1 cursor-pointer"
                          onClick={() => {

                            if (!n.is_read) {
                              markAsRead(n.id);
                            }

                            if (n.route) {
                              navigate(n.route);
                            } else {
                              navigate("/bookings");
                            }

                            setNotificationOpen(false);
                          }}
                        >

                          {/* TITLE */}
                          <div className="flex items-center gap-2">

                            <p className="text-sm font-semibold text-gray-800">
                              {n.title}
                            </p>

                            {!n.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>

                          {/* MESSAGE */}
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                            {n.message}
                          </p>

                          {/* META */}
                          <div className="flex flex-wrap gap-2 mt-3">

                            {n?.data?.patient_name && (
                              <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-1 rounded-full">
                                👤 {n.data.patient_name}
                              </span>
                            )}

                            {n?.data?.patient_phone && (
                              <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-1 rounded-full">
                                📞 {n.data.patient_phone}
                              </span>
                            )}

                            {n?.data?.appointment_date && (
                              <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-1 rounded-full">
                                📅 {n.data.appointment_date}
                              </span>
                            )}

                            {n?.data?.appointment_time && (
                              <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-1 rounded-full">
                                ⏰ {n.data.appointment_time}
                              </span>
                            )}

                          </div>
                        </div>

                        {/* DELETE */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            deleteNotification(n.id);
                          }}
                          className="
                            ml-2
                            p-1.5
                            hover:bg-red-100
                            rounded-full
                            transition
                            opacity-70 hover:opacity-100
                          "
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* ========================================================= */}
        {/* PROFILE */}
        {/* ========================================================= */}

        <div className="relative" ref={dropdownRef}>

          <button
            className="flex items-center gap-1 text-gray-700 hover:text-blue-600 text-xs"
            onClick={() =>
              setDropdownOpen(!dropdownOpen)
            }
          >
            <User className="w-4 h-4" />

            <span className="hidden sm:inline">
              {localStorage.getItem("doctor_name")}
            </span>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg p-2 z-50">

              {showProfileButton && (
                <button
                  onClick={() => navigate("/profile")}
                  className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded"
                >
                  Profile
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-100 rounded"
              >
                <LogOut className="w-3 h-3" />
                Logout
              </button>

            </div>
          )}
        </div>
      </div>

      {/* MOBILE MENU BUTTON */}
      <div className="md:hidden">

        <button
          onClick={() =>
            setMobileMenuOpen(!mobileMenuOpen)
          }
        >
          {mobileMenuOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <Menu className="w-5 h-5" />
          )}
        </button>

      </div>

      {/* MOBILE MENU */}
      {mobileMenuOpen && (
        <div className="absolute top-14 left-0 w-full bg-white shadow-md border-t z-40 flex flex-col p-3 space-y-2 md:hidden">

          {visibleButtons.map((btn, index) => (
            <button
              key={index}
              onClick={() => {
                navigate(btn.path);
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 py-1.5"
            >
              {btn.icon}
              {btn.label}
            </button>
          ))}

          {showProfileButton && (
            <button
              onClick={() => {
                navigate("/profile");
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-2 text-xs text-gray-700 hover:text-blue-600 py-1.5"
            >
              <User className="w-4 h-4" />
              Profile
            </button>
          )}

          <button
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
            className="flex items-center gap-2 text-xs text-red-600 hover:text-red-700 py-1.5"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>

        </div>
      )}
    </nav>
  );
};

export default Navbar;